<template>
  <v-dialog v-model="state.shouldShowDialog" :width="500" persistent scrollable>
    <template v-slot:activator="{ attrs }">
      <primary-button
        @click.stop="$emit('load-warehouses')"
        v-bind="attrs"
        :loading="props.loading"
        append-icon="mdi-dock-window">
        Select Export Data
      </primary-button>
    </template>
    <v-card class="dialog-box" :width="500">
      <v-card-title class="px-6 d-flex align-center">
        <span class="headline">Choose Warehouse(s) to export</span>
        <v-spacer></v-spacer>
        <v-btn class="pa-3" icon :elevation="0" @click="close">
          <v-icon v-if="!props.loading">mdi-close-circle</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="px-8 pb-6 pt-2">
        <v-container fluid>
          <v-checkbox
            hide-details="auto"
            :label="selectAllLabel"
            :input-value="allSelected"
            value
            @change="toggleSelectAll"></v-checkbox>
          <v-divider></v-divider>
          <v-checkbox
            hide-details="auto"
            v-for="warehouse in props.warehouses"
            :key="warehouse.externalId ?? warehouse.id"
            v-model="state.warehousesToExport"
            :label="warehouse.name"
            return-object
            :value="warehouse.externalId ?? warehouse.id"></v-checkbox>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <primary-button
          :loading="props.loading"
          prepend-icon="mdi-download"
          @click="exportWarehouseData">
          Export Data
        </primary-button>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
/* global defineProps, defineEmits */

import { computed, PropType, reactive, watch } from 'vue';
import PrimaryButton from '@/components/buttons/PrimaryButton.vue';
import { INovaWarehouse } from '@/interfaces/nova';

const emit = defineEmits(['close', 'load-warehouses', 'export-data']);

const props = defineProps({
  warehouses: {
    type: Array as PropType<INovaWarehouse[]>,
    required: true
  },
  loading: {
    type: Boolean,
    required: false,
    default: false
  },
  isDialogVisible: {
    type: Boolean,
    required: false,
    default: false
  }
});

const state = reactive({
  warehousesToExport: [] as string[],
  shouldShowDialog: false
});

const exportWarehouseData = () => {
  emit('export-data', state.warehousesToExport);
  close(false);
};

const allSelected = computed(() => state.warehousesToExport.length === props.warehouses.length);
const selectAllLabel = computed(() => (allSelected.value ? 'Deselect All' : 'Select All'));

const toggleSelectAll = () => {
  if (allSelected.value) {
    deselectAll();
  } else {
    selectAll();
  }
};

const selectAll = () => {
  state.warehousesToExport = props.warehouses.map(
    (warehouse: INovaWarehouse) => warehouse.externalId
  );
};

const deselectAll = () => {
  state.warehousesToExport = [];
};

const close = (shouldClearWarehouses = true) => {
  emit('close', shouldClearWarehouses);
  state.warehousesToExport = [];
};

watch(
  () => props.isDialogVisible,
  isDialogVisible => {
    state.shouldShowDialog = isDialogVisible;
  }
);
</script>
